/**
 * KOPS-534: Maricopa County Deals Dashboard
 * Navy+gold page for browsing county-sourced listing leads
 * (trusts, estates, foreclosures, absentee owners, etc.)
 *
 * Layout: When no deal selected → full table.
 *         When deal selected → collapsed address list (1/3) + detail panel (2/3).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLandmark, faFire, faHouseChimneyMedical, faGavel, faUserSlash,
  faFilter, faSync, faPhone, faChevronDown, faChevronUp,
  faMapMarkerAlt, faClock, faArrowUp, faArrowDown, faTimes,
  faExternalLinkAlt, faHistory, faClipboardList, faStickyNote,
  faChartLine, faSearch, faHome, faUsers
} from '@fortawesome/free-solid-svg-icons';
import HubShell, { GoldButton, OutlineButton } from '../broker-intelligence/shared/HubShell';
import { colors, fonts, gradients } from '../broker-intelligence/shared/colors';
import { isInternalEmployeeFlex } from '../../utils/permissions';
import countyDealService, {
  CountyDeal, CountyDealDetail, CountyDealSummary, CountyDealFilters, MLSHistory
} from '../../services/countyDealService';
import axiosInstance from '../../helpers/api';

/* ───── constants ───── */
const triggerColors: Record<string, { bg: string; text: string }> = {
  death_deed:       { bg: '#e74c3c', text: '#fff' },
  foreclosure:      { bg: '#e67e22', text: '#fff' },
  probate:          { bg: '#9b59b6', text: '#fff' },
  trust_transfer:   { bg: '#3498db', text: '#fff' },
  bank_reo:         { bg: '#e74c3c', text: '#fff' },
  quit_claim:       { bg: '#f39c12', text: '#000' },
  ownership_change: { bg: colors.navy3, text: colors.muted },
};
const tierColors: Record<string, { bg: string; text: string }> = {
  'Tier 1': { bg: '#e74c3c', text: '#fff' },
  'Tier 2': { bg: '#f39c12', text: '#000' },
  'Tier 3': { bg: colors.navy3, text: colors.muted },
};
const statusOptions = [
  { value: 'new', label: 'New' }, { value: 'reviewing', label: 'Reviewing' },
  { value: 'contacted', label: 'Contacted' }, { value: 'offer_made', label: 'Offer Made' },
  { value: 'under_contract', label: 'Under Contract' }, { value: 'closed', label: 'Closed' },
  { value: 'dismissed', label: 'Dismissed' },
];
const fmt = (v: number | null | undefined) => v != null ? `$${Number(v).toLocaleString()}` : '—';
const fmtK = (v: number | null | undefined) => v != null ? `$${(Number(v) / 1000).toFixed(0)}K` : '—';

const Badge: React.FC<{ bg: string; text: string; label: string }> = ({ bg, text, label }) => (
  <span style={{ background: bg, color: text, fontSize: '10px', fontWeight: 700, padding: '3px 8px', borderRadius: '10px', whiteSpace: 'nowrap' }}>
    {label}
  </span>
);
const DetailRow: React.FC<{ label: string; value: any; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div style={{ marginBottom: 4 }}>
    <span style={{ fontSize: 11, color: '#888', fontWeight: 500 }}>{label}: </span>
    <span style={{ fontSize: 12, color: highlight ? '#3498db' : '#333', fontWeight: highlight ? 600 : 400 }}>{String(value)}</span>
  </div>
);
const Card: React.FC<{ title?: string; icon?: any; children: React.ReactNode; style?: React.CSSProperties }> = ({ title, icon, children, style }) => (
  <div style={{ background: '#fff', borderRadius: 8, padding: 16, marginBottom: 14, border: `1px solid ${colors.border}`, ...style }}>
    {title && (
      <div style={{ fontWeight: 700, fontSize: 13, color: colors.navy, marginBottom: 10 }}>
        {icon && <FontAwesomeIcon icon={icon} style={{ marginRight: 6, color: colors.gold }} />}
        {title}
      </div>
    )}
    {children}
  </div>
);
const Stat: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <div style={{ background: colors.offBg, borderRadius: 8, padding: 10, textAlign: 'center' }}>
    <div style={{ fontSize: 11, color: colors.muted, marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: color || colors.navy }}>{value}</div>
  </div>
);

/* ───── select style ───── */
const selectStyle: React.CSSProperties = {
  background: colors.navy3, color: colors.white, border: `1px solid ${colors.navy3}`,
  borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer',
};
const linkBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', background: colors.navy, color: colors.gold2,
  border: `1px solid ${colors.gold}`, borderRadius: 6, padding: '8px 12px', fontSize: 11,
  fontWeight: 600, textDecoration: 'none', cursor: 'pointer',
};

/* ════════════════════════════════════════════════════════════════════ */
const CountyDealsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  /* ───── access control ───── */
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  useEffect(() => {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token) { setAuthorized(false); return; }
      const payload = JSON.parse(atob(token.split('.')[1]));
      setAuthorized(isInternalEmployeeFlex(payload));
    } catch { setAuthorized(false); }
  }, []);

  /* ───── state ───── */
  const [deals, setDeals] = useState<CountyDeal[]>([]);
  const [summary, setSummary] = useState<CountyDealSummary | null>(null);
  const [filterOptions, setFilterOptions] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [enrichMsg, setEnrichMsg] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDeals, setTotalDeals] = useState(0);
  const [sortCol, setSortCol] = useState('lead_score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [searchText, setSearchText] = useState('');

  // Filters
  const [fTier, setFTier] = useState('');
  const [fTrigger, setFTrigger] = useState('');
  const [fCity, setFCity] = useState('');
  const [fStatus, setFStatus] = useState('all');
  const [fCategory, setFCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Detail
  const [selectedDeal, setSelectedDeal] = useState<CountyDealDetail | null>(null);
  const [mlsHistory, setMlsHistory] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState<'overview' | 'comps' | 'history' | 'notes'>('overview');
  const [cmaLoading, setCmaLoading] = useState(false);
  const [cmaData, setCmaData] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  // Call state
  const [ownerPhone, setOwnerPhone] = useState('');
  const [callers, setCallers] = useState<any[]>([]);
  const [selectedCaller, setSelectedCaller] = useState('');
  const [calling, setCalling] = useState(false);
  const [callMsg, setCallMsg] = useState('');

  // Create lead state
  const [creatingLead, setCreatingLead] = useState(false);
  const [leadCreated, setLeadCreated] = useState<string | null>(null);
  const [leadContactId, setLeadContactId] = useState<string | null>(null);

  /* ───── data fetching ───── */
  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const filters: CountyDealFilters = { page, per_page: 25, sort: sortCol, order: sortDir, status: fStatus || 'all' };
      if (fTier) filters.tier = fTier;
      if (fTrigger) filters.trigger_type = fTrigger;
      if (fCity) filters.city = fCity;
      if (fCategory) filters.category = fCategory;
      const res = await countyDealService.getDeals(filters);
      if (res.success) {
        setDeals(res.data.deals);
        setTotalPages(res.data.pagination.total_pages);
        setTotalDeals(res.data.pagination.total);
      }
    } catch (e) { console.error('Failed to fetch:', e); }
    finally { setLoading(false); }
  }, [page, sortCol, sortDir, fTier, fTrigger, fCity, fStatus, fCategory]);

  useEffect(() => { countyDealService.getSummary().then(r => r.success && setSummary(r.data)).catch(() => {}); }, []);

  // Load deal from URL param on mount
  useEffect(() => {
    const dealId = searchParams.get('dealId');
    if (dealId && !selectedDeal) {
      openDetail(parseInt(dealId));
    }
  }, []);
  useEffect(() => { countyDealService.getFilters().then(r => r.success && setFilterOptions(r.data)).catch(() => {}); }, []);
  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const openDetail = async (id: number) => {
    setSearchParams({ dealId: String(id) });
    setDetailLoading(true); setCmaData(null); setDetailTab('overview');
    try {
      const res = await countyDealService.getDeal(id);
      if (res.success) {
        setSelectedDeal(res.data.deal);
        setMlsHistory(res.data.mls_history || []);
        setNotes(res.data.deal.status_notes || '');
        setOwnerPhone(res.data.deal.owner_phone || '');
        setCallMsg('');
        setLeadCreated(null);
        setLeadContactId(res.data.linked_contact_id || null);
        setNotesSaved(false);
      }
    } catch (e) { console.error(e); }
    finally { setDetailLoading(false); }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await countyDealService.updateStatus(id, newStatus);
      fetchDeals();
      if (selectedDeal?.id === id) setSelectedDeal(prev => prev ? { ...prev, status: newStatus } : null);
    } catch (e) { console.error(e); }
  };

  const handleEnrich = async () => {
    setEnriching(true); setEnrichMsg('');
    try { const res = await countyDealService.enrichMLS(); setEnrichMsg(res.message || 'Done'); fetchDeals(); }
    catch { setEnrichMsg('Failed'); }
    finally { setEnriching(false); }
  };

  const handleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
    setPage(1);
  };

  const resetFilters = () => { setFTier(''); setFTrigger(''); setFCity(''); setFStatus('all'); setFCategory(''); setSearchText(''); setPage(1); };

  const fetchCMA = async (deal: CountyDealDetail) => {
    if (!deal.property_address) return;
    setCmaLoading(true); setCmaData(null);
    try {
      const address = `${deal.property_address}, ${deal.property_city}, AZ ${deal.property_zip}`;
      // Use the same address-analysis endpoint as the client hub "Run CMA" button.
      // It needs a lead_id — use a test lead to avoid creating DB records.
      const { data } = await axiosInstance.post('/sellers/enhanced-cma/address-analysis', {
        address,
        lead_id: 'county-deal-' + deal.id,
        user_id: 'county-deals-system',
        seller_profile_data: {
          bedrooms: deal.bedrooms || 3,
          bathrooms: deal.bathrooms || 2,
          square_footage: deal.living_area || 1500,
          year_built: deal.year_built || 1980,
        }
      }, { timeout: 120000 }); // 2 min timeout — CMA can take a while
      console.log('CMA response:', data);
      setCmaData(data);
    } catch (err: any) {
      console.error('CMA error:', err?.response?.data || err?.message || err);
      setCmaData({ error: err?.response?.data?.error || 'Comp analysis failed. Property may not have enough data.' });
    }
    finally { setCmaLoading(false); }
  };

  const saveNotes = async () => {
    if (!selectedDeal) return;
    setNotesSaving(true);
    try {
      await axiosInstance.put(`/county-deals/${selectedDeal.id}/notes`, { notes });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    } catch { /* ignore */ }
    finally { setNotesSaving(false); }
  };

  // Fetch team callers
  useEffect(() => {
    axiosInstance.get('/contacts/team-callers').then(r => {
      if (r.data.success) setCallers(r.data.callers || []);
    }).catch(() => {});
  }, []);

  const handleCall = async () => {
    if (!selectedDeal || !ownerPhone) { setCallMsg('Enter phone number first'); return; }
    const caller = callers.find(c => c.user_phone === selectedCaller) || callers[0];
    if (!caller) { setCallMsg('No team callers available'); return; }
    setCalling(true); setCallMsg('');
    try {
      const { data } = await axiosInstance.post(`/county-deals/${selectedDeal.id}/call`, {
        callerPhone: caller.user_phone,
        callerName: `${caller.user_first_name} ${caller.user_last_name}`,
        phoneToCall: ownerPhone,
      });
      if (data.success) setCallMsg('Call initiated — check your phone');
      else setCallMsg(data.error || 'Call failed');
    } catch (e: any) { setCallMsg(e.response?.data?.error || 'Call failed'); }
    finally { setCalling(false); }
  };

  const savePhone = async () => {
    if (!selectedDeal || !ownerPhone) return;
    try {
      await axiosInstance.put(`/county-deals/${selectedDeal.id}/phone`, { phone: ownerPhone });
      setSelectedDeal(prev => prev ? { ...prev, owner_phone: ownerPhone } : null);
    } catch { /* ignore */ }
  };

  const handleCreateLead = async () => {
    if (!selectedDeal) return;
    setCreatingLead(true); setLeadCreated(null); setLeadContactId(null);
    try {
      const { data } = await axiosInstance.post(`/county-deals/${selectedDeal.id}/create-lead`);
      if (data.success) {
        setLeadCreated(data.existing ? 'Already exists' : 'Lead created');
        setLeadContactId(data.contact_id || null);
        fetchDeals();
      } else if (data.contact_id) {
        setLeadCreated('Already exists');
        setLeadContactId(data.contact_id);
      } else { setLeadCreated(data.error || 'Failed'); }
    } catch (e: any) { setLeadCreated(e.response?.data?.error || 'Failed to create lead'); }
    finally { setCreatingLead(false); }
  };

  // Client-side text filter
  const filteredDeals = searchText.trim()
    ? deals.filter(d => {
        const q = searchText.toLowerCase();
        return d.property_address?.toLowerCase().includes(q) || d.current_owner?.toLowerCase().includes(q) ||
          d.apn?.includes(q) || d.property_city?.toLowerCase().includes(q) || d.property_zip?.includes(q) ||
          d.trigger_type?.toLowerCase().includes(q) || d.deed_type_label?.toLowerCase().includes(q);
      })
    : deals;

  const isExpanded = !!selectedDeal;

  /* ───── access denied ───── */
  if (authorized === false) return (
    <div style={{ background: colors.navy, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: fonts.body }}>
      <div style={{ textAlign: 'center', color: colors.white }}>
        <h2 style={{ color: colors.gold2, marginBottom: 12 }}>Access Restricted</h2>
        <p style={{ color: colors.muted }}>County Deals is available to KairosIQ Realty agents only.</p>
        <a href="/agent-portal/dashboard" style={{ color: colors.gold2, textDecoration: 'underline' }}>Back to Dashboard</a>
      </div>
    </div>
  );
  if (authorized === null) return <div style={{ background: colors.navy, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.muted }}>Loading...</div>;

  /* ───── HubShell config ───── */
  const metrics = summary ? [
    { icon: faLandmark, value: summary.total_deals, label: 'Total Leads' },
    { icon: faFire, value: summary.tier1_count, label: 'Tier 1', color: '#e74c3c' },
    { icon: faGavel, value: summary.foreclosures + summary.death_deeds, label: 'Distressed', color: '#e67e22' },
    { icon: faUserSlash, value: summary.absentee_count, label: 'Absentee', color: '#3498db' },
    { icon: faHouseChimneyMedical, value: summary.trust_transfers, label: 'Trust/Estate', color: '#9b59b6' },
  ] : [];

  return (
    <HubShell breadcrumb="County Deals" brandMiddle="County" metrics={metrics}
      tabs={[{ key: 'deals', label: 'County Deals', icon: faLandmark, badge: totalDeals || null }]}
      activeTab="deals" onTabChange={() => {}} homeHref="/agent-portal/dashboard"
      actions={<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {enrichMsg && <span style={{ color: colors.green, fontSize: 12 }}>{enrichMsg}</span>}
        <GoldButton onClick={() => {
          const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
          window.open(`/api/county-deals/export?tier=Tier+1&limit=5000&token=${token}`, '_blank');
        }}>
          Export Tier 1
        </GoldButton>
        <OutlineButton onClick={handleEnrich} disabled={enriching}>
          <FontAwesomeIcon icon={faSync} spin={enriching} style={{ marginRight: 6 }} />{enriching ? 'Enriching...' : 'Enrich MLS'}
        </OutlineButton>
        <OutlineButton onClick={() => setShowFilters(f => !f)}>
          <FontAwesomeIcon icon={faFilter} style={{ marginRight: 6 }} />Filters
        </OutlineButton>
      </div>}
    >
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ═══ LEFT PANEL ═══ */}
        <div style={{
          width: isExpanded ? '33%' : '100%', minWidth: isExpanded ? 320 : 'auto',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'width 0.2s ease',
          borderRight: isExpanded ? `1px solid ${colors.navy3}` : 'none',
        }}>
          {/* Search */}
          <div style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            <FontAwesomeIcon icon={faSearch} style={{ color: colors.muted, fontSize: 12 }} />
            <input type="text" placeholder="Search address, owner, APN..." value={searchText} onChange={e => setSearchText(e.target.value)}
              style={{ flex: 1, background: 'transparent', border: 'none', color: colors.white, fontSize: 13, outline: 'none' }} />
            {searchText && <button onClick={() => setSearchText('')} style={{ background: 'none', border: 'none', color: colors.muted, cursor: 'pointer' }}><FontAwesomeIcon icon={faTimes} /></button>}
          </div>

          {/* Filters */}
          {showFilters && filterOptions && (
            <div style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '8px 12px', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
              <select value={fTier} onChange={e => { setFTier(e.target.value); setPage(1); }} style={selectStyle}>
                <option value="">All Tiers</option>
                {filterOptions.tiers?.map((t: string) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={fTrigger} onChange={e => { setFTrigger(e.target.value); setPage(1); }} style={selectStyle}>
                <option value="">All Triggers</option>
                {filterOptions.triggers?.map((t: any) => <option key={t.trigger_type} value={t.trigger_type}>{t.trigger_type.replace(/_/g, ' ')} ({t.cnt})</option>)}
              </select>
              <select value={fCity} onChange={e => { setFCity(e.target.value); setPage(1); }} style={selectStyle}>
                <option value="">All Cities</option>
                {filterOptions.cities?.map((c: any) => <option key={c.city} value={c.city}>{c.city} ({c.cnt})</option>)}
              </select>
              <select value={fStatus} onChange={e => { setFStatus(e.target.value); setPage(1); }} style={selectStyle}>
                <option value="all">All Statuses</option>
                {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <button onClick={resetFilters} style={{ background: 'none', border: 'none', color: colors.muted, cursor: 'pointer', fontSize: 11 }}>
                <FontAwesomeIcon icon={faTimes} /> Clear
              </button>
            </div>
          )}

          {/* Deal list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: colors.muted }}>Loading...</div>
            ) : isExpanded ? (
              /* ─── Collapsed list view ─── */
              filteredDeals.map(d => {
                const active = selectedDeal?.id === d.id;
                const tc = triggerColors[d.trigger_type] || triggerColors.ownership_change;
                return (
                  <div key={d.id} onClick={() => openDetail(d.id)} style={{
                    padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${colors.navy3}`,
                    background: active ? colors.navy3 : 'transparent', transition: 'background 0.1s',
                  }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = colors.navy2; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: colors.white, fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {d.property_address}
                        </div>
                        <div style={{ fontSize: 11, color: colors.muted, marginTop: 2 }}>
                          {d.property_city} {d.property_zip} — {d.current_owner?.slice(0, 25)}{(d.current_owner?.length || 0) > 25 ? '...' : ''}
                          {d.property_type && (
                            <span style={{ marginLeft: 6, color: d.property_type?.toUpperCase().includes('RESIDENTIAL') ? '#27ae60' : '#f39c12', fontWeight: 600 }}>
                              {d.property_type?.toUpperCase().includes('RESIDENTIAL') ? 'RES' : 'COM'}
                            </span>
                          )}
                          {d.deed_date && (
                            <span style={{ marginLeft: 6, color: colors.gold2 }}>
                              {new Date(d.deed_date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, marginLeft: 8 }}>
                        <Badge {...tc} label={d.trigger_type.replace(/_/g, ' ')} />
                        <span style={{ fontSize: 11, fontWeight: 700, color: d.lead_score >= 50 ? '#e74c3c' : d.lead_score >= 25 ? '#f39c12' : colors.muted }}>
                          {d.lead_score}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              /* ─── Full table view ─── */
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ position: 'sticky', top: 0, background: colors.navy, zIndex: 2 }}>
                    {[['lead_score','Score'],['','Tier'],['','Trigger'],['property_address','Address'],['','Type'],['','Owner'],['deed_date','Deed Date'],['','Deed'],['assessed_value','Assessed'],['as_is_value','MLS Value'],['','Beds/Ba/SqFt'],['','Status']].map(([col, label]) => (
                      <th key={label} onClick={() => col && handleSort(col)} style={{
                        padding: '10px 12px', cursor: col ? 'pointer' : 'default', fontSize: 11, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.muted,
                        borderBottom: `1px solid ${colors.navy3}`, whiteSpace: 'nowrap', userSelect: 'none',
                      }}>
                        {label}
                        {col && sortCol === col && <FontAwesomeIcon icon={sortDir === 'desc' ? faArrowDown : faArrowUp} style={{ marginLeft: 4, fontSize: 9, color: colors.gold2 }} />}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredDeals.map(d => {
                    const tc = triggerColors[d.trigger_type] || triggerColors.ownership_change;
                    const tierC = tierColors[d.lead_tier] || tierColors['Tier 3'];
                    return (
                      <tr key={d.id} onClick={() => openDetail(d.id)} style={{ cursor: 'pointer', borderBottom: `1px solid ${colors.navy3}` }}
                        onMouseEnter={e => e.currentTarget.style.background = colors.navy2}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={tdS}><span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '50%', background: d.lead_score >= 50 ? '#e74c3c' : d.lead_score >= 25 ? '#f39c12' : colors.navy3, color: d.lead_score >= 25 ? '#fff' : colors.muted, fontWeight: 700, fontSize: 11 }}>{d.lead_score}</span></td>
                        <td style={tdS}><Badge {...tierC} label={d.lead_tier} /></td>
                        <td style={tdS}><Badge {...tc} label={d.trigger_type.replace(/_/g, ' ')} /></td>
                        <td style={{ ...tdS, color: colors.white, fontWeight: 500 }}>{d.property_address}<div style={{ fontSize: 11, color: colors.muted }}>{d.property_city} {d.property_zip}</div></td>
                        <td style={tdS}><span style={{ fontSize: 10, fontWeight: 700, color: d.property_type?.toUpperCase().includes('RESIDENTIAL') ? '#27ae60' : '#f39c12' }}>{d.property_type?.toUpperCase().includes('RESIDENTIAL') ? 'Residential' : d.property_type || '—'}</span></td>
                        <td style={{ ...tdS, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.current_owner}{d.is_absentee ? <div style={{ fontSize: 10, color: '#3498db' }}><FontAwesomeIcon icon={faMapMarkerAlt} /> Absentee</div> : null}</td>
                        <td style={tdS}>{d.deed_date ? new Date(d.deed_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}</td>
                        <td style={tdS}><span style={{ fontWeight: 600 }}>{d.deed_type || '—'}</span><div style={{ fontSize: 10, color: colors.muted }}>{d.deed_type_label}</div></td>
                        <td style={{ ...tdS, fontFamily: 'monospace', fontSize: 12 }}>{fmtK(d.assessed_value)}</td>
                        <td style={{ ...tdS, fontFamily: 'monospace', fontSize: 12, color: d.as_is_value ? colors.green : colors.muted }}>{fmtK(d.as_is_value)}</td>
                        <td style={{ ...tdS, fontSize: 12 }}>{d.bedrooms ?? '—'}/{d.bathrooms ?? '—'}/{d.living_area ?? '—'}</td>
                        <td style={tdS}>
                          <select value={d.status} onChange={e => { e.stopPropagation(); handleStatusChange(d.id, e.target.value); }} onClick={e => e.stopPropagation()}
                            style={{ background: colors.navy3, color: colors.white, border: `1px solid ${colors.navy3}`, borderRadius: 4, padding: '4px 6px', fontSize: 11, cursor: 'pointer' }}>
                            {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredDeals.length === 0 && <tr><td colSpan={12} style={{ padding: 40, textAlign: 'center', color: colors.muted }}>No deals found.</td></tr>}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          <div style={{ background: colors.navy2, borderTop: `1px solid ${colors.navy3}`, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <span style={{ color: colors.muted, fontSize: 11 }}>{totalDeals} leads — Page {page}/{totalPages}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <OutlineButton onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ fontSize: 10, padding: '4px 8px' }}>Prev</OutlineButton>
              <OutlineButton onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ fontSize: 10, padding: '4px 8px' }}>Next</OutlineButton>
            </div>
          </div>
        </div>

        {/* ═══ RIGHT: DETAIL PANEL (2/3) ═══ */}
        {selectedDeal && (
          <div style={{ flex: 1, background: colors.offBg, overflowY: 'auto', color: '#333' }}>
            {detailLoading ? (
              <div style={{ padding: 60, textAlign: 'center', color: colors.muted }}>Loading deal...</div>
            ) : (<>
              {/* Header */}
              <div style={{ background: '#fff', padding: '18px 24px', borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 22, color: colors.navy, fontWeight: 700 }}>{selectedDeal.property_address}</h2>
                    <div style={{ color: colors.muted, fontSize: 14, marginTop: 4 }}>
                      {selectedDeal.property_city}, AZ {selectedDeal.property_zip} — APN: {selectedDeal.apn}
                    </div>
                  </div>
                  <button onClick={() => { setSelectedDeal(null); setSearchParams({}); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: colors.muted, padding: 8 }}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Badge {...(tierColors[selectedDeal.lead_tier] || tierColors['Tier 3'])} label={selectedDeal.lead_tier} />
                  <Badge {...(triggerColors[selectedDeal.trigger_type] || triggerColors.ownership_change)} label={selectedDeal.trigger_type.replace(/_/g, ' ')} />
                  <span style={{ background: colors.navy, color: colors.gold2, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10 }}>Score: {selectedDeal.lead_score}</span>
                  {selectedDeal.deed_date && (
                    <span style={{ background: '#1a1a2e', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FontAwesomeIcon icon={faClock} style={{ fontSize: 10 }} />
                      Deed: {new Date(selectedDeal.deed_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                  <select value={selectedDeal.status} onChange={e => handleStatusChange(selectedDeal.id, e.target.value)}
                    style={{ marginLeft: 'auto', background: colors.navy, color: colors.white, border: `1px solid ${colors.gold}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Tab bar */}
              <div style={{ display: 'flex', background: '#fff', borderBottom: `1px solid ${colors.border}` }}>
                {([
                  { key: 'overview', label: 'Overview', icon: faHome },
                  { key: 'comps', label: 'Comps & Valuation', icon: faChartLine },
                  { key: 'history', label: `MLS History (${mlsHistory.length})`, icon: faHistory },
                  { key: 'notes', label: 'Notes', icon: faStickyNote },
                ] as const).map(tab => (
                  <button key={tab.key} onClick={() => {
                    setDetailTab(tab.key);
                    if (tab.key === 'comps' && !cmaData && !cmaLoading) fetchCMA(selectedDeal);
                  }} style={{
                    flex: 1, padding: '12px 0', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    background: detailTab === tab.key ? colors.offBg : '#fff',
                    color: detailTab === tab.key ? colors.navy : colors.muted,
                    borderBottom: detailTab === tab.key ? `3px solid ${colors.gold}` : '3px solid transparent',
                  }}>
                    <FontAwesomeIcon icon={tab.icon} style={{ marginRight: 5 }} />{tab.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div style={{ padding: 20 }}>

                {/* ─── OVERVIEW ─── */}
                {detailTab === 'overview' && (<>
                  {/* Quick stats row */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
                    <Stat label="Assessed" value={fmtK(selectedDeal.assessed_value)} />
                    <Stat label="MLS Value" value={fmtK(selectedDeal.as_is_value)} color={selectedDeal.as_is_value ? colors.green : undefined} />
                    <Stat label="Year Built" value={selectedDeal.year_built?.toString() || '—'} />
                    <Stat label="Ownership" value={selectedDeal.ownership_years ? `${selectedDeal.ownership_years}yr` : '—'} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <Card title="Score Breakdown" icon={faClipboardList}>
                      {selectedDeal.score_reasons?.split('; ').map((r, i) => (
                        <div key={i} style={{ fontSize: 12, color: '#555', padding: '2px 0' }}>• {r}</div>
                      ))}
                    </Card>
                    <Card title="Owner Information">
                      <DetailRow label="Owner" value={selectedDeal.current_owner} />
                      {selectedDeal.previous_owner && <DetailRow label="Previous" value={selectedDeal.previous_owner} />}
                      <DetailRow label="Category" value={selectedDeal.ownership_category?.replace(/_/g, ' ')} />
                      <DetailRow label="Deed" value={`${selectedDeal.deed_type} — ${selectedDeal.deed_type_label}`} />
                      {selectedDeal.is_absentee ? <DetailRow label="Mailing" value={selectedDeal.mailing_address || `${selectedDeal.mailing_city}, ${selectedDeal.mailing_state}`} highlight /> : null}
                    </Card>
                  </div>

                  <Card title="Property Details">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                      <DetailRow label="Type" value={selectedDeal.property_type} />
                      <DetailRow label="Beds" value={selectedDeal.bedrooms ?? '—'} />
                      <DetailRow label="Baths" value={selectedDeal.bathrooms ?? '—'} />
                      <DetailRow label="SqFt" value={selectedDeal.living_area?.toLocaleString() ?? '—'} />
                      <DetailRow label="Lot" value={selectedDeal.lot_size?.toLocaleString() ?? '—'} />
                      <DetailRow label="Year Built" value={selectedDeal.year_built ?? '—'} />
                      <DetailRow label="Assessed" value={fmt(selectedDeal.assessed_value)} />
                      <DetailRow label="MLS Value" value={fmt(selectedDeal.as_is_value)} />
                    </div>
                  </Card>

                  {/* Data timestamps */}
                  <div style={{ background: colors.offBg, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 11, color: '#888', display: 'flex', gap: 20 }}>
                    {selectedDeal.deed_date && <span><strong>Deed Recorded:</strong> {new Date(selectedDeal.deed_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>}
                    {selectedDeal.first_detected_at && <span><strong>First Detected:</strong> {new Date(selectedDeal.first_detected_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                    {selectedDeal.last_scanned_at && <span><strong>Last Scanned:</strong> {new Date(selectedDeal.last_scanned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                  </div>

                  {/* Call / Phone section */}
                  <Card title="Contact Owner" icon={faPhone}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                      <input type="tel" placeholder="Enter owner phone number..."
                        value={ownerPhone} onChange={e => setOwnerPhone(e.target.value)}
                        style={{ flex: 1, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '8px 12px', fontSize: 13, outline: 'none' }}
                        onBlur={savePhone}
                      />
                      {ownerPhone && (
                        <select value={selectedCaller} onChange={e => setSelectedCaller(e.target.value)}
                          style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: '8px', fontSize: 12 }}>
                          {callers.map((c: any) => (
                            <option key={c.id} value={c.user_phone}>{c.user_first_name} {c.user_last_name}</option>
                          ))}
                        </select>
                      )}
                      {ownerPhone && (
                        <button onClick={handleCall} disabled={calling}
                          style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          <FontAwesomeIcon icon={faPhone} style={{ marginRight: 4 }} />
                          {calling ? 'Calling...' : 'Call Now'}
                        </button>
                      )}
                    </div>
                    {callMsg && <div style={{ fontSize: 12, color: callMsg.includes('initiated') ? '#27ae60' : '#e74c3c', marginBottom: 8 }}>{callMsg}</div>}
                    {selectedDeal.owner_phone && <div style={{ fontSize: 11, color: '#888' }}>Saved: {selectedDeal.owner_phone}</div>}
                  </Card>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {/* Skip trace — free people search */}
                    <a href={`https://www.truepeoplesearch.com/results?name=${encodeURIComponent(selectedDeal.current_owner?.replace(/TRUST|LLC|INC|CORP|REVOCABLE|IRREVOCABLE|LIVING|FAMILY|ESTATE OF|THE /gi, '').trim())}&citystatezip=${encodeURIComponent(selectedDeal.mailing_city && selectedDeal.mailing_state ? selectedDeal.mailing_city + ', ' + selectedDeal.mailing_state : selectedDeal.property_city + ', AZ')}`}
                      target="_blank" rel="noopener noreferrer" style={{ ...linkBtnStyle, background: '#27ae60', color: '#fff', border: '1px solid #27ae60' }}>
                      <FontAwesomeIcon icon={faPhone} style={{ marginRight: 4 }} /> Lookup Phone
                    </a>
                    <a href={`https://www.fastpeoplesearch.com/name/${encodeURIComponent(selectedDeal.current_owner?.replace(/TRUST|LLC|INC|CORP|REVOCABLE|IRREVOCABLE|LIVING|FAMILY|ESTATE OF|THE /gi, '').trim().toLowerCase().replace(/\s+/g, '-'))}`}
                      target="_blank" rel="noopener noreferrer" style={{ ...linkBtnStyle, background: '#2980b9', color: '#fff', border: '1px solid #2980b9' }}>
                      <FontAwesomeIcon icon={faSearch} style={{ marginRight: 4 }} /> FastPeople
                    </a>
                    <a href={`https://treasurer.maricopa.gov/PropertyTaxInformation/?Parcel=${selectedDeal.apn}`} target="_blank" rel="noopener noreferrer" style={linkBtnStyle}>
                      <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginRight: 4 }} /> Treasurer
                    </a>
                    <a href={`https://mcassessor.maricopa.gov/parcel/${selectedDeal.apn}`} target="_blank" rel="noopener noreferrer" style={linkBtnStyle}>
                      <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginRight: 4 }} /> Assessor
                    </a>
                    <GoldButton onClick={() => { setDetailTab('comps'); if (!cmaData && !cmaLoading) fetchCMA(selectedDeal); }}>
                      <FontAwesomeIcon icon={faChartLine} style={{ marginRight: 4 }} /> Run Comp Analysis
                    </GoldButton>
                    <button onClick={handleCreateLead} disabled={creatingLead}
                      style={{ display: 'inline-flex', alignItems: 'center', background: '#8e44ad', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faUsers} style={{ marginRight: 4 }} />
                      {creatingLead ? 'Creating...' : 'Import as Lead'}
                    </button>
                    {leadCreated && (
                      <span style={{ fontSize: 12, color: leadCreated.includes('Failed') ? '#e74c3c' : '#27ae60', alignSelf: 'center', fontWeight: 600 }}>
                        {leadCreated}
                      </span>
                    )}
                    {leadContactId && (
                      <a href={`/agent-portal/clients?id=${leadContactId}&tab=overview`}
                        style={{ display: 'inline-flex', alignItems: 'center', background: colors.navy, color: colors.gold2, border: `1px solid ${colors.gold}`, borderRadius: 6, padding: '8px 14px', fontSize: 11, fontWeight: 700, textDecoration: 'none', cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginRight: 4 }} /> View in Client Hub
                      </a>
                    )}
                  </div>
                </>)}

                {/* ─── COMPS & VALUATION ─── */}
                {detailTab === 'comps' && (<>
                  {cmaLoading && <div style={{ padding: 40, textAlign: 'center', color: colors.muted }}><div style={{ fontSize: 14, marginBottom: 8 }}>Running comp analysis...</div><div style={{ fontSize: 11 }}>Geocoding → MLS lookup → XGBoost prediction → comparable sales</div></div>}
                  {cmaData?.error && <Card><div style={{ color: '#856404', fontSize: 13 }}>{cmaData.error}</div></Card>}
                  {cmaData && !cmaData.error && (<>
                    {cmaData.analysis_results?.xgboost_prediction && (
                      <Card title="AI Price Prediction (XGBoost)">
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                          <Stat label="Predicted Value" value={fmt(cmaData.analysis_results.xgboost_prediction.predicted_price)} color={colors.green} />
                          <Stat label="Confidence" value={`${((cmaData.analysis_results.enhanced_confidence || cmaData.analysis_results.xgboost_prediction.confidence || 0) * 100).toFixed(0)}%`} />
                          <Stat label="Assessed Value" value={fmt(selectedDeal.assessed_value)} />
                        </div>
                        {selectedDeal.assessed_value && cmaData.analysis_results.xgboost_prediction.predicted_price && (
                          <div style={{ fontSize: 12, color: '#555', padding: '8px 0', borderTop: `1px solid ${colors.border}` }}>
                            Predicted is <strong>{((Number(cmaData.analysis_results.xgboost_prediction.predicted_price) / Number(selectedDeal.assessed_value) - 1) * 100).toFixed(1)}%</strong>
                            {Number(cmaData.analysis_results.xgboost_prediction.predicted_price) > Number(selectedDeal.assessed_value) ? ' above' : ' below'} county assessed value
                          </div>
                        )}
                      </Card>
                    )}

                    {cmaData.analysis_results?.comparables?.length > 0 && (
                      <Card title={`Comparable Sales (${cmaData.analysis_results.comparables.length})`}>
                        {cmaData.analysis_results.statistics && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
                            <Stat label="Median Price" value={fmt(cmaData.analysis_results.statistics.median_price)} />
                            <Stat label="Avg $/SqFt" value={`$${(cmaData.analysis_results.statistics.median_ppsf || cmaData.analysis_results.statistics.avg_price_per_sqft || 0).toFixed(0)}`} />
                            <Stat label="Avg DOM" value={`${(cmaData.analysis_results.statistics.avg_days_on_market || 0).toFixed(0)}`} />
                          </div>
                        )}
                        {cmaData.analysis_results.comparables.map((comp: any, i: number) => (
                          <div key={i} style={{ padding: '12px 0', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between' }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>{comp.unparsed_address || comp.address}</div>
                              <div style={{ color: '#777', fontSize: 12, marginTop: 2 }}>{comp.bedrooms}bd / {comp.bathrooms}ba / {comp.living_area?.toLocaleString()}sqft{comp.year_built ? ` / Built ${comp.year_built}` : ''}</div>
                              {comp.distance_miles != null && <div style={{ color: '#999', fontSize: 11, marginTop: 2 }}>{comp.distance_miles?.toFixed(2)} mi away{comp.close_date ? ` — Closed ${new Date(comp.close_date).toLocaleDateString()}` : ''}</div>}
                              {comp.listing_id && <div style={{ color: '#aaa', fontSize: 10, marginTop: 1 }}>MLS# {comp.listing_id}</div>}
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                              <div style={{ fontWeight: 700, color: colors.green, fontSize: 15 }}>{fmt(comp.close_price || comp.list_price)}</div>
                              {comp.living_area && comp.close_price && <div style={{ fontSize: 11, color: '#999' }}>${(comp.close_price / comp.living_area).toFixed(0)}/sqft</div>}
                            </div>
                          </div>
                        ))}
                      </Card>
                    )}
                  </>)}
                  {!cmaLoading && !cmaData && (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <GoldButton onClick={() => fetchCMA(selectedDeal)}><FontAwesomeIcon icon={faChartLine} style={{ marginRight: 6 }} /> Run Comp Analysis</GoldButton>
                      <div style={{ color: colors.muted, fontSize: 12, marginTop: 8 }}>XGBoost price prediction + comparable sales</div>
                    </div>
                  )}
                </>)}

                {/* ─── MLS HISTORY ─── */}
                {detailTab === 'history' && (<>
                  {mlsHistory.length > 0 ? (
                    <Card title={`MLS Listing History — ${mlsHistory.length} records`} icon={faHistory}>
                      {mlsHistory.map((m: any, i: number) => (
                        <div key={i} style={{ padding: '14px 0', borderTop: i > 0 ? `1px solid ${colors.border}` : 'none' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                                <span style={{
                                  background: m.standard_status === 'Closed' ? colors.green : m.standard_status === 'Active' ? '#3498db' : colors.muted,
                                  color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                                }}>{m.standard_status}</span>
                                <span style={{ fontSize: 11, color: '#999' }}>MLS# {m.listing_id}</span>
                              </div>
                              <div style={{ fontSize: 12, color: '#555' }}>{m.unparsed_address}</div>
                              <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>
                                {m.bedrooms ?? '—'}bd / {m.bathrooms ?? '—'}ba / {m.living_area?.toLocaleString() ?? '—'}sqft
                                {m.lot_size_sqft ? ` / ${m.lot_size_sqft.toLocaleString()} lot` : ''}
                                {m.year_built ? ` / Built ${m.year_built}` : ''}
                                {m.pool_yn ? ' / Pool' : ''}
                                {m.garage_spaces ? ` / ${m.garage_spaces}G` : ''}
                              </div>
                              {m.days_on_market != null && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{m.days_on_market} days on market</div>}
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                              <div style={{ fontWeight: 700, color: colors.green, fontSize: 16 }}>{m.close_price ? fmt(m.close_price) : fmt(m.list_price)}</div>
                              {m.original_list_price && m.original_list_price !== m.list_price && (
                                <div style={{ fontSize: 11, color: '#999', textDecoration: 'line-through' }}>List: {fmt(m.original_list_price)}</div>
                              )}
                              {m.close_date && <div style={{ fontSize: 11, color: '#999' }}>{new Date(m.close_date).toLocaleDateString()}</div>}
                            </div>
                          </div>
                          {(m.list_agent_name || m.buyer_agent_name) && (
                            <div style={{ marginTop: 8, padding: '8px 10px', background: colors.offBg, borderRadius: 6, fontSize: 11 }}>
                              {m.list_agent_name && <div><strong>List Agent:</strong> {m.list_agent_name} ({m.list_office_name}) {m.list_agent_mls_id ? `— MLS# ${m.list_agent_mls_id}` : ''}</div>}
                              {m.buyer_agent_name && <div><strong>Buyer Agent:</strong> {m.buyer_agent_name} ({m.buyer_office_name}) {m.buyer_agent_mls_id ? `— MLS# ${m.buyer_agent_mls_id}` : ''}</div>}
                            </div>
                          )}
                          {m.public_remarks && (
                            <div style={{ marginTop: 8, fontSize: 11, color: '#777', lineHeight: 1.5, maxHeight: 60, overflow: 'hidden' }}>
                              {m.public_remarks.slice(0, 300)}{m.public_remarks.length > 300 ? '...' : ''}
                            </div>
                          )}
                        </div>
                      ))}
                    </Card>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: colors.muted }}>
                      <FontAwesomeIcon icon={faHistory} style={{ fontSize: 24, marginBottom: 10, display: 'block' }} />
                      No MLS listing history found for this parcel.
                      <br /><span style={{ fontSize: 12 }}>Try running MLS Enrichment from the header.</span>
                    </div>
                  )}
                </>)}

                {/* ─── NOTES ─── */}
                {detailTab === 'notes' && (<>
                  <Card title="Deal Notes" icon={faStickyNote}>
                    <textarea
                      value={notes}
                      onChange={e => { setNotes(e.target.value); setNotesSaved(false); }}
                      placeholder="Add notes about this deal — outreach attempts, follow-up items, owner contact info..."
                      style={{
                        width: '100%', minHeight: 180, border: `1px solid ${colors.border}`, borderRadius: 6,
                        padding: 12, fontSize: 13, fontFamily: fonts.body, resize: 'vertical', outline: 'none',
                      }}
                      onFocus={e => { e.currentTarget.style.borderColor = colors.gold; }}
                      onBlur={e => { e.currentTarget.style.borderColor = colors.border; }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                      <GoldButton onClick={saveNotes} disabled={notesSaving}>
                        {notesSaving ? 'Saving...' : 'Save Notes'}
                      </GoldButton>
                      {notesSaved && <span style={{ color: colors.green, fontSize: 12 }}>Saved</span>}
                    </div>
                  </Card>

                  <Card title="Call Log">
                    {selectedDeal.call_notes ? (
                      <pre style={{ fontSize: 12, color: '#555', whiteSpace: 'pre-wrap', margin: 0 }}>{selectedDeal.call_notes}</pre>
                    ) : (
                      <div style={{ color: colors.muted, fontSize: 12 }}>No calls logged yet.</div>
                    )}
                  </Card>
                </>)}

              </div>
            </>)}
          </div>
        )}
      </div>
    </HubShell>
  );
};

const tdS: React.CSSProperties = { padding: '10px 12px', color: colors.muted, fontSize: 13, verticalAlign: 'middle' };

export default CountyDealsPage;
